import { useEffect, useRef, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import type { OfficeHost } from '../office/bootstrap';
import type { ContextScope, InsertMode } from '../office/context';
import { readContext, insertResult, type DocumentContext } from '../office/context';
import { agentChat, autonomyBadge, autonomyChat } from '../api/client';
import { MessageBubble, type ChatMessage } from './MessageBubble';
import { QuickActions } from './QuickActions';
import { ContextPanel } from './ContextPanel';

interface Props {
  host: OfficeHost;
  /**
   * Deployed AgentCore runtime id for the host-pinned agent. Already
   * resolved by AppShell via useHostAgentId — never a blueprint id here.
   * If absent, ChatPanel falls back to Brain/autonomy chat.
   */
  agentId?: string;
}

export function ChatPanel({ host, agentId }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [scope, setScope] = useState<ContextScope>('selection');
  const [busy, setBusy] = useState(false);
  const [contextPreview, setContextPreview] = useState<DocumentContext | null>(null);
  const [perAgentSessionId, setPerAgentSessionId] = useState<string | undefined>(undefined);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isChatOnly = host === 'Teams' || host === 'Meet' || host === 'Unknown';
  const isTeams = isChatOnly; // legacy alias

  // Refresh the context preview whenever the user changes scope.
  useEffect(() => {
    if (isTeams) {
      setContextPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const preview = await readContext(host, scope);
        if (!cancelled) setContextPreview(preview);
      } catch {
        if (!cancelled) setContextPreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [host, scope, isTeams]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  function appendMessage(m: ChatMessage) {
    setMessages((prev) => [...prev, m]);
  }
  function patchLast(patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.slice();
      next[next.length - 1] = { ...next[next.length - 1], ...patch };
      return next;
    });
  }

  async function send(promptOverride?: string, scopeOverride?: ContextScope) {
    const userText = (promptOverride ?? input).trim();
    if (!userText || busy) return;
    setBusy(true);
    if (!promptOverride) setInput('');

    const useScope = scopeOverride ?? scope;
    let docContext: DocumentContext = { label: '', text: '', approxTokens: 0, truncated: false };
    if (!isTeams) {
      try {
        docContext = await readContext(host, useScope);
      } catch {
        // Non-fatal; fall through with empty context.
      }
    }

    const fullPrompt = composePrompt(host, userText, docContext);

    appendMessage({
      id: crypto.randomUUID(),
      role: 'user',
      text: docContext.text
        ? `${userText}\n\n[+ ${docContext.label}]`
        : userText,
    });
    appendMessage({ id: crypto.randomUUID(), role: 'assistant', text: '', pending: true });

    const res = agentId
      ? await agentChat(agentId, fullPrompt, perAgentSessionId)
      : await autonomyChat(fullPrompt);
    if (res.error || !res.data) {
      patchLast({ pending: false, text: `**Error:** ${res.error || 'No response'}` });
    } else if (agentId) {
      const data = res.data;
      if (!('message' in data)) {
        patchLast({ pending: false, text: '**Error:** Unexpected agent response' });
        setBusy(false);
        return;
      }
      patchLast({
        pending: false,
        text: data.message || '_(empty response)_',
        badge: hostLabelForBadge(host),
      });
      setPerAgentSessionId(data.sessionId);
    } else {
      const data = res.data;
      if (!('action' in data)) {
        patchLast({ pending: false, text: '**Error:** Unexpected Brain response' });
        setBusy(false);
        return;
      }
      patchLast({
        pending: false,
        text: data.reply || '_(empty response)_',
        badge: autonomyBadge(data),
      });
    }

    setBusy(false);
  }

  async function handleInsert(mode: InsertMode, text: string, html?: string) {
    try {
      await insertResult(host, mode, text, html);
    } catch (e) {
      appendMessage({
        id: crypto.randomUUID(),
        role: 'system',
        text: `Insert failed: ${e instanceof Error ? e.message : 'unknown error'}`,
      });
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-ink-100/30 px-3 py-3">
        {messages.length === 0 ? (
          <EmptyState host={host} />
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} msg={m} host={host} onInsert={handleInsert} />
          ))
        )}
      </div>

      <div className="space-y-2 border-t border-ink-100 bg-white px-3 py-2.5">
        <QuickActions
          host={host}
          disabled={busy}
          onPick={(prompt, qScope) => {
            setScope(qScope);
            send(prompt, qScope);
          }}
        />

        <ContextPanel scope={scope} onScopeChange={setScope} preview={contextPreview} hidden={isTeams} />

        <div className="flex items-end gap-1.5">
          <textarea
            rows={2}
            value={input}
            placeholder={placeholderFor(host)}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            className="min-h-[44px] flex-1 resize-y rounded-md border border-ink-100 px-2.5 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-brand-500 text-white shadow-soft hover:bg-brand-600 disabled:opacity-50"
            title="Send (⌘/Ctrl + Enter)"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function placeholderFor(host: OfficeHost): string {
  switch (host) {
    case 'Word':       return 'Ask the agent to draft, edit, or summarize…';
    case 'Excel':      return 'Ask for a formula, an analysis, or a transformation…';
    case 'PowerPoint': return 'Ask for slide content, speaker notes, or an outline…';
    case 'Teams':      return 'Ask anything — brainstorm, draft, summarize…';
    case 'Meet':       return 'Ask for an agenda, summary, or a quick lookup…';
    default:           return 'Ask anything…';
  }
}

function composePrompt(host: OfficeHost, userText: string, ctx: DocumentContext): string {
  if (!ctx.text) return userText;
  const fence = host === 'Excel' ? 'tsv' : 'text';
  return [
    userText,
    '',
    `--- ${ctx.label} ---`,
    '```' + fence,
    ctx.text,
    '```',
  ].join('\n');
}

function hostLabelForBadge(host: OfficeHost): string {
  switch (host) {
    case 'Word':       return 'Word agent';
    case 'Excel':      return 'Excel agent';
    case 'PowerPoint': return 'PowerPoint agent';
    default:           return host;
  }
}

function EmptyState({ host }: { host: OfficeHost }) {
  const tips: Record<OfficeHost, string[]> = {
    Word: [
      'Highlight a paragraph and tap **Tighten writing**.',
      'Ask "Summarize this document for an exec" with **Whole doc** context.',
      'Pin a specific agent (e.g. legal-reviewer) for domain answers.',
    ],
    Excel: [
      'Select a range, then ask "What does this data tell us?".',
      'Tap **Generate formula** and describe what you need in plain English.',
      'Use **Clean column** to standardize messy text values in place.',
    ],
    PowerPoint: [
      'Open a slide and tap **Speaker notes**.',
      'Ask "Rewrite this slide for a board audience".',
      'Use **Outline a deck** to expand a single slide into a full deck.',
    ],
    Teams: [
      'Use fallback chat for general questions, brainstorming, and drafting.',
      'No document context is attached for this host.',
    ],
    Meet: [
      'Use fallback chat for agendas, summaries, and follow-up drafts.',
      'No meeting transcript is attached automatically yet.',
    ],
    Outlook: [
      'Use fallback chat now; Outlook-specific document actions are on the way.',
      'Paste the email text you want help with.',
    ],
    Unknown: [
      'Use fallback chat for general questions while the Office host is not detected.',
      'Open inside Word, Excel, or PowerPoint for document-aware actions.',
    ],
  };
  return (
    <div className="mx-auto mt-6 max-w-xs rounded-xl border border-dashed border-ink-100 bg-white p-4 text-center shadow-soft">
      <div className="mb-2 text-2xl">✨</div>
      <h3 className="text-sm font-semibold text-ink-900">Your AI agents, in {host}</h3>
      <ul className="mt-3 space-y-1.5 text-left text-[12px] text-ink-700">
        {tips[host].map((t, i) => {
          // Strings are hardcoded above, but escape defensively so a future
          // refactor that pipes in user-controlled text cannot inject HTML.
          const escaped = t
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
          const formatted = escaped.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
          return (
            <li key={i} className="flex gap-2">
              <span className="text-brand-500">•</span>
              <span dangerouslySetInnerHTML={{ __html: formatted }} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
